import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import {Box, Dialog, IconButton, Stack, Tooltip} from "@mui/material";
import Typography from "@mui/material/Typography";
import {useRef} from "react";
import {useTranslation} from "react-i18next";
import SurveyQrCode from "./SurveyQrCode";
import {downloadSurveyQrCode} from "./surveyQrDownload.util";

interface Props {
    open: boolean
    onClose: () => void
    value: string
    surveyName?: string
    /** Brand mark for the handover, resolved by `useSurveyQrLogoUrl` - absent for accounts with none. */
    logoUrl?: string
}

const QR_SIZE = 'min(70vw, 70vh)'

/**
 * Poster-sized, not hero-sized: the logo tops the hierarchy but the code stays the thing you look
 * at. Matches the cap the runner uses for the same logo, so the handover and the page it opens
 * present the mark at the same weight.
 */
const LOGO_MAX_HEIGHT = 72

/** Comfortably past the 48px touch guideline - staff hit this on a tablet, often one-handed. */
const CORNER_BUTTON_SIZE = 56

/**
 * The handover screen: a tablet is turned towards the guest, so nothing of the product may be
 * left on it - no app chrome, no guest name, and above all no link text. The guest scans, nothing
 * else. Staff exit with the corner button or Escape; a stray touch on a handed-over tablet must
 * not close it, which is why the backdrop is deliberately inert.
 *
 * This screen is also the only place that still offers the PNG download, so the corner carries two
 * controls. They are muted to stay out of the guest's way but sized for a fingertip on a tablet:
 * {@link CORNER_BUTTON_SIZE}px targets, set in from the very corner where a bezel grip lands.
 *
 * Branding reads top-down like a poster - logo, survey name, code, hint - so the guest sees whose
 * code this is before they raise the phone. Accounts without any logo render exactly as before:
 * the logo is omitted outright rather than reserving an empty slot in the stack.
 */
const SurveyQrFullscreenDialog = ({open, onClose, value, surveyName, logoUrl}: Props) => {

    const {t} = useTranslation()
    const wrapperRef = useRef<HTMLDivElement>(null)

    return (
        <Dialog
            open={open}
            fullScreen
            onClose={(_event, reason) => {
                if (reason === 'backdropClick') return
                onClose()
            }}
            PaperProps={{sx: {backgroundColor: 'background.default'}}}
        >
            <Stack
                direction={'row'}
                gap={1}
                sx={{position: 'absolute', top: 20, right: 20, zIndex: 1}}
            >
                <Tooltip title={t('Download QR code')}>
                    <IconButton
                        aria-label={t('Download QR code')}
                        onClick={() => downloadSurveyQrCode(wrapperRef.current, surveyName || 'survey', logoUrl)}
                        sx={{width: CORNER_BUTTON_SIZE, height: CORNER_BUTTON_SIZE, color: 'text.secondary'}}
                    >
                        <DownloadIcon fontSize={'large'}/>
                    </IconButton>
                </Tooltip>

                <Tooltip title={t('Close')}>
                    <IconButton
                        aria-label={t('Close')}
                        onClick={onClose}
                        sx={{width: CORNER_BUTTON_SIZE, height: CORNER_BUTTON_SIZE, color: 'text.secondary'}}
                    >
                        <CloseIcon fontSize={'large'}/>
                    </IconButton>
                </Tooltip>
            </Stack>

            <Stack
                alignItems={'center'}
                justifyContent={'center'}
                gap={3}
                sx={{flex: 1, p: 4, overflow: 'auto'}}
            >
                {!!logoUrl && (
                    <Box
                        component={'img'}
                        src={logoUrl}
                        alt={''}
                        sx={{
                            display: 'block',
                            maxHeight: LOGO_MAX_HEIGHT,
                            maxWidth: 'min(60%, 320px)',
                            objectFit: 'contain',
                        }}
                    />
                )}

                {!!surveyName && (
                    <Typography
                        variant={'h4'}
                        fontWeight={600}
                        textAlign={'center'}
                        sx={{maxWidth: '90%'}}
                    >
                        {surveyName}
                    </Typography>
                )}

                <SurveyQrCode ref={wrapperRef} value={value} size={QR_SIZE} padding={3} logoUrl={logoUrl}/>

                <Typography variant={'body1'} color={'text.secondary'} textAlign={'center'}>
                    {t('Scan the code with your phone camera')}
                </Typography>
            </Stack>
        </Dialog>
    )
}

export default SurveyQrFullscreenDialog
